/**
 * atelier-brush.js — the visitor's brush.
 *
 * On a precise pointer the cursor becomes a brush: a small wet cherry
 * tip above the page, and pigment daubs pressed into a persistent wet
 * layer BEHIND the content (only on pages that carry the living
 * painting, so paint lands on the artwork and never over ordinary
 * copy). The wet layer dries slowly via a destination-out fade with a
 * periodic stronger pass, so trails melt back into the painting.
 *
 * Never blocks clicks (both canvases are pointer-events:none), never
 * runs on touch devices, and disappears entirely at the reduced motion
 * tier, where the native cursor returns via CSS.
 */
(() => {
  const boot = (A) => {
    const fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;
    if (!fine) return;
    /* The games own their pointer language — never paint over them. */
    if (document.body && document.body.hasAttribute('data-immersive')) return;

    const wet = document.createElement('canvas');
    wet.className = 'atl-wet';
    wet.setAttribute('aria-hidden', 'true');
    const tip = document.createElement('canvas');
    tip.className = 'atl-tip';
    tip.setAttribute('aria-hidden', 'true');
    document.body.append(wet, tip);

    const SCALE = 0.5;
    const wctx = wet.getContext('2d');
    const tctx = tip.getContext('2d');
    const size = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      for (const c of [wet, tip]) {
        c.width = Math.ceil(w * SCALE);
        c.height = Math.ceil(h * SCALE);
        c.style.width = w + 'px';
        c.style.height = h + 'px';
      }
    };
    size();
    window.addEventListener('resize', size);

    const syncClass = () => {
      document.documentElement.classList.toggle('atl-brush-on', A.motion.allows());
    };
    syncClass();
    A.motion.onChange(syncClass);

    /* Two palettes for two grounds.
       On the old noir painting (atl-canvas-on): maroon body, cherry
       emotion, rare cotton and lit flecks compositing additively.
       On the lit cotton pages (data-atl-wet / atl-cinema-on): cotton
       daubs would vanish into cotton, so the brush carries maroon and
       cherry almost exclusively, with a rare rose fleck. */
    const PIGMENTS_DARK = [
      { rgb: '99,0,0', w: 0.48, light: false },
      { rgb: '129,1,0', w: 0.32, light: false },
      { rgb: '192,80,58', w: 0.12, light: true },
      { rgb: '237,235,221', w: 0.08, light: true },
    ];
    const PIGMENTS_LIGHT = [
      { rgb: '99,0,0', w: 0.56, light: false },
      { rgb: '129,1,0', w: 0.3, light: false },
      { rgb: '58,36,34', w: 0.08, light: false },
      { rgb: '217,169,162', w: 0.06, light: false },
    ];
    /* The wet layer paints on: the retired noir canvas, the cinematic
       homepage, and any lit editorial page that opts in via body
       [data-atl-wet]. Everywhere else the brush stays dry. */
    const ground = () => {
      const html = document.documentElement;
      if (html.classList.contains('atl-canvas-on')) return 'dark';
      if (html.classList.contains('atl-cinema-on')) return 'light';
      if (document.body && document.body.hasAttribute('data-atl-wet')) return 'light';
      return null;
    };
    const pick = (set) => {
      let roll = Math.random();
      for (const p of set) {
        roll -= p.w;
        if (roll <= 0) return p;
      }
      return set[0];
    };

    let mx = -100;
    let my = -100;
    let lastX = -100;
    let lastY = -100;
    let gx = -100;
    let gy = -100;
    let speed = 0;
    let hoverGlow = 0;
    let hoverTarget = 0;
    let wetFrames = 0;
    let raf = null;
    let tick = 0;

    const INTERACTIVE = 'a, button, [role="button"], input, textarea, select, summary, label';

    const daub = (x, y, dx, dy) => {
      if (!A.motion.allows()) return;
      const g = ground();
      if (!g) return;
      const swell = 1 + Math.min(speed / 55, 0.9);
      const len = (9 + Math.random() * 15) * swell;
      const squash = 0.3 + Math.random() * 0.22;
      const ang = Math.atan2(dy, dx) + (Math.random() - 0.5) * 0.5;
      const p = pick(g === 'dark' ? PIGMENTS_DARK : PIGMENTS_LIGHT);
      /* On cotton the paint is thinner — about 40% less pigment — so
         trails read as washes, never as stains over the copy. */
      const a = g === 'dark' ? 0.05 + Math.random() * 0.08 : 0.03 + Math.random() * 0.048;
      wctx.save();
      wctx.translate(x * SCALE, y * SCALE);
      wctx.rotate(ang);
      wctx.scale(1, squash);
      if (p.light) wctx.globalCompositeOperation = 'lighter';
      const r = len * SCALE;
      const grad = wctx.createRadialGradient(0, 0, 0, 0, 0, r);
      grad.addColorStop(0, 'rgba(' + p.rgb + ',' + a.toFixed(3) + ')');
      grad.addColorStop(0.7, 'rgba(' + p.rgb + ',' + (a * 0.45).toFixed(3) + ')');
      grad.addColorStop(1, 'rgba(' + p.rgb + ',0)');
      wctx.fillStyle = grad;
      wctx.beginPath();
      wctx.arc(0, 0, r, 0, Math.PI * 2);
      wctx.fill();
      wctx.restore();
      wetFrames = Math.max(wetFrames, 320);
    };

    const emit = (x0, y0, x1, y1) => {
      const dx = x1 - x0;
      const dy = y1 - y0;
      const dist = Math.hypot(dx, dy);
      const steps = Math.max(1, Math.min(Math.floor(dist / 8), 12));
      for (let i = 1; i <= steps; i++) {
        const t = i / steps;
        daub(x0 + dx * t, y0 + dy * t, dx, dy);
      }
    };

    const loop = () => {
      tick++;

      if (wetFrames > 0) {
        wetFrames--;
        wctx.save();
        wctx.setTransform(1, 0, 0, 1, 0, 0);
        wctx.globalCompositeOperation = 'destination-out';
        wctx.fillStyle = tick % 40 === 0 ? 'rgba(0,0,0,0.09)' : 'rgba(0,0,0,0.028)';
        wctx.fillRect(0, 0, wet.width, wet.height);
        wctx.restore();
      }

      hoverGlow += (hoverTarget - hoverGlow) * 0.12;
      gx += (mx - gx) * 0.35;
      gy += (my - gy) * 0.35;
      tctx.clearRect(0, 0, tip.width, tip.height);
      if (mx >= 0 && A.motion.allows()) {
        const hx = gx * SCALE;
        const hy = gy * SCALE;
        const r = (5 + Math.min(speed * 0.07, 4) + hoverGlow * 2.5) * SCALE;
        const halo = tctx.createRadialGradient(hx, hy, 0, hx, hy, r * 3.2);
        halo.addColorStop(0, 'rgba(192,80,58,0.85)');
        halo.addColorStop(0.45, 'rgba(129,1,0,0.4)');
        halo.addColorStop(1, 'rgba(129,1,0,0)');
        tctx.fillStyle = halo;
        tctx.beginPath();
        tctx.arc(hx, hy, r * 3.2, 0, Math.PI * 2);
        tctx.fill();
        tctx.fillStyle = 'rgba(246,243,231,0.92)';
        tctx.beginPath();
        tctx.arc(hx, hy, Math.max(r * 0.42, 1), 0, Math.PI * 2);
        tctx.fill();
        if (hoverGlow > 0.02) {
          tctx.strokeStyle = 'rgba(217,169,162,' + (hoverGlow * 0.8).toFixed(3) + ')';
          tctx.lineWidth = 1.1;
          tctx.beginPath();
          tctx.arc(hx, hy, r * 4.4, 0, Math.PI * 2);
          tctx.stroke();
        }
      }

      speed *= 0.86;
      const busy = wetFrames > 0 || hoverGlow > 0.01 || speed > 0.4;
      raf = busy ? window.requestAnimationFrame(loop) : null;
    };

    document.addEventListener(
      'pointermove',
      (e) => {
        mx = e.clientX;
        my = e.clientY;
        if (lastX < 0) {
          lastX = mx;
          lastY = my;
          gx = mx;
          gy = my;
        }
        hoverTarget = e.target.closest(INTERACTIVE) ? 1 : 0;
        speed = Math.min(Math.hypot(mx - lastX, my - lastY), 120);
        emit(lastX, lastY, mx, my);
        lastX = mx;
        lastY = my;
        if (!raf) loop();
      },
      { passive: true }
    );

    document.addEventListener('pointerdown', (e) => {
      for (let i = 0; i < 9; i++) {
        const ang = Math.random() * Math.PI * 2;
        const dist = Math.random() * 22;
        daub(e.clientX + Math.cos(ang) * dist, e.clientY + Math.sin(ang) * dist, Math.cos(ang) * 20, Math.sin(ang) * 20);
      }
      if (!raf) loop();
    });

    document.addEventListener('pointerleave', () => {
      hoverTarget = 0;
      mx = -100;
      my = -100;
      lastX = -100;
      if (!raf) loop();
    });
  };

  if (window.Atelier && window.Atelier.__ready) boot(window.Atelier);
  else document.addEventListener('atelier:ready', (e) => boot(e.detail), { once: true });
})();
